import { useTranslate } from '@bynder/localization';
import { SettingsAccordion } from '../../SettingsAccordion';
import { useActiveWidget } from '../../../../../queries/guides';
import { CardWidget } from '../../../../../types';
import { useInspectorStore } from '../../../../../stores/inspectorStore';
import { useCardWidgetMutations } from '../../../../../mutations/widgets/card';
import { useEffect, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { Checkbox, Input, List, Thumbnail } from '@bynder/design-system';
import { INSPECTOR_DEBOUNCE_DELAY } from '../../constants';
import { IconLink } from '@bynder/icons';
import { isValidUrl } from '../../../../../helpers/utils';

export const CardLinkEditor = () => {
  const { translate } = useTranslate();
  const activeWidget = useActiveWidget() as CardWidget;
  const widgetSettings = useInspectorStore(s => s.widgetSettings);
  const card = activeWidget?.cards[widgetSettings?.widgetIndex];

  const { editLink } = useCardWidgetMutations();

  const handleEditLink = (url: string, openNewTab: boolean) => {
    editLink.mutate({
      cardId: card.id,
      cardIndex: widgetSettings?.widgetIndex,
      url,
      openNewTab,
    });
  };
  return (
    <SettingsAccordion
      thumbnail={<Thumbnail icon={<IconLink />} />}
      title={translate('WIDGET_INSPECTOR.CARD_CUSTOM_LINK_ACCORDION')}
    >
      <InspectorPanelUrlEditor
        url={card?.link.url}
        openNewTab={card?.link.openNewTab}
        handleChange={handleEditLink}
      />
    </SettingsAccordion>
  );
};

const InspectorPanelUrlEditor = ({
  url,
  openNewTab,
  handleChange,

  placeholder,
}: {
  url: string;
  openNewTab: boolean;
  handleChange: (link: string, openNewTab?: boolean) => void;
  placeholder?: string;
}) => {
  const { translate } = useTranslate();

  const onSetLink = (link: string) => {
    handleChange(link, openNewTab);
  };

  const [link, setLink] = useState(url || '');
  const debouncedSetLink = useDebouncedCallback(
    onSetLink,
    INSPECTOR_DEBOUNCE_DELAY,
  );
  const handleLinkChange = (value: string) => {
    setLink(value);

    if (isValidUrl(value) || value === '') {
      debouncedSetLink(value);
    }
  };

  const handleOpenInNewTabChange = () => {
    handleChange(link, !openNewTab);
  };

  const handleOnBlur = () => {
    debouncedSetLink.cancel();
  };

  useEffect(() => {
    setLink(url);
  }, [url]);

  return (
    <List.Item>
      <Input
        data-testid="cardLink"
        onBlur={handleOnBlur}
        isInvalid={!isValidUrl(link)}
        placeholder={
          placeholder || translate('WIDGET_INSPECTOR.BUTTON_LINK_PLACEHOLDER')
        }
        value={link}
        onChange={handleLinkChange}
      />
      <Checkbox isChecked={openNewTab} onChange={handleOpenInNewTabChange}>
        {translate('WIDGETS_INSPECTOR.IMAGE_NEW_TAB')}
      </Checkbox>
    </List.Item>
  );
};
